'use client';

import type { ReactNode } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';

import { LINE, RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { HubInfoTip } from './hub-info-tip';

/**
 * 허브의 한 구역 — 제목 · ⓘ 기준 안내 · 한 줄 설명 · 「더 보기」.
 *
 * 허브는 **훑는 자리**다. 깊이 파고드는 일은 각 구역의 「더 보기」가 원래 화면으로 넘긴다 —
 * 그래야 허브가 모든 기능을 흉내 내다 비대해지지 않는다.
 * ⚠️ 그림자를 두지 않는다 — 카드마다 그림자가 있으면 구역들이 서로 떠 보인다(테두리 하나로 충분하다).
 */
export function HubSection({
  title,
  hint,
  info,
  href,
  hrefLabel,
  action,
  dense = false,
  sx,
  children,
}: {
  title: string;
  /** 제목 아래 한 줄 — 숫자를 바로 읽게 하는 수·기간만(「최근 30일 · 채널별」 · 「1,105개 중 4개」) */
  hint?: string;
  /** 제목 옆 ⓘ 툴팁의 글머리 줄 — 집계 기준 · 적용 안 되는 필터 · 정렬(`HubInfoTip`) */
  info?: readonly string[];
  /** 「더 보기」가 가는 곳 — 없으면 단추를 두지 않는다 */
  href?: string;
  hrefLabel?: string;
  /** 오른쪽에 붙는 것 — 「더 보기」 **앞**에 선다(둘 다 둘 수 있다) */
  action?: ReactNode;
  /** 순위 목록처럼 촘촘한 내용일 때 — 안쪽 여백을 줄인다 */
  dense?: boolean;
  /** 바깥 배치에 맡기는 것만 — 격자에서 몇 칸을 쓸지 같은 것(`gridColumn`) */
  sx?: SxProps<Theme>;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        px: dense ? 1.75 : 2,
        py: dense ? 1.5 : 2,
        borderRadius: RADIUS.lg,
        border: '1px solid',
        borderColor: LINE.edge,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: dense ? 1.25 : 1.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="uiTitle">{title}</Typography>
            <HubInfoTip items={info} />
          </Stack>
          {hint && (
            <Typography noWrap variant="uiMeta" color="text.disabled" sx={{ mt: 0.125 }}>{hint}</Typography>
          )}
        </Box>
        {action}
        {href && (
          <Button
            component={RouterLink}
            href={href}
            size="small"
            color="inherit"
            endIcon={<Iconify icon="solar:alt-arrow-right-linear" width={14} />}
            sx={{ flexShrink: 0, height: 26, px: 1, borderRadius: RADIUS.pill, typography: 'uiAction', color: 'text.secondary' }}
          >
            {hrefLabel ?? '더 보기'}
          </Button>
        )}
      </Stack>
      {children}
    </Box>
  );
}
